import {
  ReactNode,
  Ref,
  RefObject,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import useResizeObserver, {
  UseResizeObserverCallback,
} from "@react-hook/resize-observer"
import { IconInfoTriangle } from "@tabler/icons-react"
import {
  Cell,
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  PaginationState,
  TableOptions as ReactTableOptions,
  Row,
  RowSelectionState,
  SortingState,
  Table,
  TableMeta,
  VisibilityState,
} from "@tanstack/react-table"
import { notUndefined, useVirtualizer } from "@tanstack/react-virtual"
import { useInView } from "react-intersection-observer"
import { useDebounceValue } from "usehooks-ts"

import { useContainerSize } from "@/hooks/use-container-size"
import { cn } from "@/lib/utils"

import { Checkbox } from "../Checkbox"
import { EmptyState } from "../EmptyState"
import * as RadioGroup from "../RadioGroup"
import { Spinner } from "../Spinner"
import { ClearFiltersButton } from "./actions/ClearFiltersButton"
import { Toolbar } from "./actions/Toolbar"
import { Memo_TableBodyRow } from "./body/TableBodyRow"
import { BulkActionsFloater } from "./BulkActionsFloater"
import { ActiveFiltersList } from "./filters/ActiveFiltersList"
import { FilterButton } from "./filters/FilterButton"
import { GlobalFilter } from "./filters/GlobalFilter"
import { Footer } from "./footer/Footer"
import { TableHead, TablePosition } from "./header/TableHead"
import { LE_TableInstance, LE_TableOptions } from "./types"
import { useLumenTable } from "./useTable"

export interface DataTableProps<TData extends Record<string, any> = object> {
  id?: string
  data: TData[]
  columns: ColumnDef<TData, any>[]

  isLoading?: boolean
  hasError?: boolean
  fetchMore?: () => void
  total?: number
  getIsRowLoading?: (row: Row<TData>) => boolean
  getRowId?: LE_TableOptions<TData>["getRowId"]
  activeRowId?: string
  emptyState?: ReactNode
  errorState?: ReactNode
  onRowClick?: (row: Row<TData>) => void
  className?: string
  classNameHeader?: string
  classNameFooter?: string
  classNameCell?: string | ((cell: Cell<TData, unknown>) => string)
  tableContainerClassName?: string

  sorting?: SortingState
  defaultSorting?: SortingState
  onSortingChange?: LE_TableOptions<TData>["onSortingChange"]
  manualSorting?: boolean

  columnVisibility?: VisibilityState
  defaultColumnVisibility?: VisibilityState
  /** Adding this handler will make the columnVisibility state controlled */
  onColumnVisibilityChange?: LE_TableOptions<TData>["onColumnVisibilityChange"]

  columnFilters?: ColumnFiltersState
  defaultColumnFilters?: ColumnFiltersState
  onColumnFiltersChange?: LE_TableOptions<TData>["onColumnFiltersChange"]
  manualFiltering?: boolean

  pagination?: PaginationState
  defaultPagination?: PaginationState
  onPaginationChange?: LE_TableOptions<TData>["onPaginationChange"]
  manualPagination?: boolean
  enablePagination?: boolean

  rowSelection?: RowSelectionState
  defaultRowSelection?: RowSelectionState
  onRowSelectionChange?: LE_TableOptions<TData>["onRowSelectionChange"]
  enableRowSelection?: boolean
  enableMultiRowSelection?: boolean
  renderBulkActions?: LE_TableOptions<TData>["renderBulkActions"]
  rowSelectedText?: string

  columnPinning?: ColumnPinningState
  defaultColumnPinning?: ColumnPinningState
  /** Adding this handler will make the columnPinning state controlled */
  onColumnPinningChange?: LE_TableOptions<TData>["onColumnPinningChange"]

  globalFilter?: string
  /** Initial global filter value */
  defaultGlobalFilter?: string
  /** Adding this handler will make the globalFilter state controlled */
  onGlobalFilterChange?: ReactTableOptions<TData>["onGlobalFilterChange"]

  renderKebabMenuContent?: (item: TData) => ReactNode
  renderCustomActions?: (args: {
    table: LE_TableInstance<TData>
    data: TData[]
  }) => ReactNode
  limit?: number
  tableLayout?: "fixed" | "auto"

  enableColumnVisibility?: boolean
  enableFullscreenToggle?: boolean
  showFilters?: boolean
  showGlobalSearch?: boolean | "always"
  showFooter?: boolean
  showColumnKebabMenu?: boolean
  debounceGlobalSearch?: boolean | number
  virtualizeRows?: boolean

  /** This is a function that returns the rows to be exported, to be used when the table is server-side paginated */
  getExportRows?: () => Promise<TData[]>
  enableExportButton?: boolean
  exportFilename?: string

  onUpdateData?: TableMeta<TData>["updateData"]

  tableRef?: Ref<LE_TableInstance<TData>>

  customRowRender?: (row: Row<TData>) => ReactNode
}

export const SELECTABLE_COLUMN_ID = "selectable-column"
export const KEBAB_MENU_COLUMN_ID = "kebab-menu-column"

export function DataTable<TData extends Record<string, any> = object>({
  id,
  data,
  columns: externalColumns,
  isLoading = false,
  hasError = false,
  fetchMore,
  total,
  getIsRowLoading,
  getRowId,
  activeRowId,
  emptyState = (
    <EmptyState
      icon={IconInfoTriangle}
      title="No data available"
      className="rounded-none"
    />
  ),
  errorState,
  onRowClick,

  className,
  classNameHeader,
  classNameFooter,
  classNameCell,
  tableContainerClassName,

  sorting,
  defaultSorting = [],
  onSortingChange,
  manualSorting,

  columnVisibility,
  defaultColumnVisibility,
  onColumnVisibilityChange,

  columnFilters,
  defaultColumnFilters = [],
  onColumnFiltersChange,
  manualFiltering,

  pagination,
  defaultPagination,
  onPaginationChange,
  manualPagination,
  enablePagination = false,

  rowSelection,
  defaultRowSelection = {},
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection = false,
  renderBulkActions,
  rowSelectedText,

  columnPinning,
  defaultColumnPinning: defaultColumnPinningProp = {
    left: [SELECTABLE_COLUMN_ID],
    right: [KEBAB_MENU_COLUMN_ID],
  },
  onColumnPinningChange,

  globalFilter,
  defaultGlobalFilter,
  onGlobalFilterChange,

  renderKebabMenuContent,
  renderCustomActions,
  limit,
  tableLayout = "auto",

  enableColumnVisibility = true,
  enableFullscreenToggle = false,
  showFilters = false,
  showGlobalSearch = false,
  showFooter = false,
  showColumnKebabMenu = false,
  debounceGlobalSearch = false,
  virtualizeRows = false,

  enableExportButton = false,
  getExportRows,
  exportFilename,

  onUpdateData,

  tableRef: instanceRef,

  customRowRender,
}: DataTableProps<TData>) {
  const [containerWidth, setContainerWidth] = useDebounceValue(0, 100)
  const [activeMoreMenuRowId, setActiveMoreMenuRowId] = useState<string>()

  const { ref: inViewRef } = useInView({
    skip: !fetchMore,
    onChange(inView) {
      if (inView && !isLoading) {
        fetchMore?.()
      }
    },
  })
  const tableRef = useRef<HTMLTableElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const leftBodyRef = useRef<HTMLTableSectionElement>(null)
  const centerBodyRef = useRef<HTMLTableSectionElement>(null)
  const rightBodyRef = useRef<HTMLTableSectionElement>(null)

  const leftBodySize = useContainerSize(leftBodyRef)
  const centerBodySize = useContainerSize(centerBodyRef)
  const rightBodySize = useContainerSize(rightBodyRef)

  const innerTotalWidth =
    leftBodySize.width + centerBodySize.width + rightBodySize.width

  const tableContainerResizeCallback = useCallback<UseResizeObserverCallback>(
    (entry) => {
      setContainerWidth(entry.target.clientWidth)
    },
    [setContainerWidth],
  )
  useResizeObserver(tableContainerRef, tableContainerResizeCallback)

  const columns = useMemo(() => {
    let cols = [...externalColumns]

    const selectCol: ColumnDef<TData> = {
      id: SELECTABLE_COLUMN_ID,
      size: 48,
      maxSize: 48,
      minSize: 48,
      meta: {
        fixedSize: true,
      },
      header: ({ table }) => {
        if (!enableMultiRowSelection) return null
        const checked =
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() ? "indeterminate" : false)
        return (
          <Checkbox
            checked={checked}
            onCheckedChange={(checked) => table.toggleAllPageRowsSelected(!!checked)}
            className="pt-0.5"
          />
        )
      },
      cell: ({ row }) => {
        if (!enableMultiRowSelection)
          return (
            <RadioGroup.Root
              className="p-1"
              value={row.getIsSelected() ? row.id : ""}
              onValueChange={() => row.toggleSelected()}
              onClick={(event) => event.stopPropagation()}
            >
              <RadioGroup.Item value={row.id} checked={row.getIsSelected()} />
            </RadioGroup.Root>
          )

        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={() => row.toggleSelected()}
          />
        )
      },
    }

    if (renderKebabMenuContent) {
      cols.push({
        id: KEBAB_MENU_COLUMN_ID,
        size: 42,
        enableHiding: false,
        meta: {
          fixedSize: true,
        },
      })
    }

    if (enableRowSelection) cols = [selectCol, ...cols]
    return cols
  }, [
    externalColumns,
    renderKebabMenuContent,
    enableRowSelection,
    enableMultiRowSelection,
  ])

  const defaultColumnPinning = useMemo(() => {
    if (renderKebabMenuContent) {
      const right = defaultColumnPinningProp.right ?? []
      if (!right.includes(KEBAB_MENU_COLUMN_ID)) {
        return {
          ...defaultColumnPinningProp,
          right: [...right, KEBAB_MENU_COLUMN_ID],
        }
      }
    }
    return defaultColumnPinningProp
  }, [renderKebabMenuContent, defaultColumnPinningProp])

  const tableOptions: LE_TableOptions<TData> = {
    data,
    columns,
    onSortingChange,
    onColumnVisibilityChange,
    onColumnFiltersChange,
    onColumnPinningChange,
    onPaginationChange,
    onRowSelectionChange,
    manualFiltering,
    manualPagination,
    manualSorting,
    enablePagination,
    enableRowSelection,
    enableMultiRowSelection,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      columnPinning,
      globalFilter,
      pagination,
      rowSelection,
      total: total ?? data.length,
    },
    initialState: {
      columnVisibility: defaultColumnVisibility,
      columnFilters: defaultColumnFilters,
      sorting: defaultSorting,
      globalFilter: defaultGlobalFilter || "",
      pagination: defaultPagination,
      rowSelection: defaultRowSelection,
      columnPinning: defaultColumnPinning,
    },
    onGlobalFilterChange,
    tableId: id,
    debounceGlobalSearch,
    getRowId,
    renderBulkActions,
    isLoading,
    getExportRows,
    meta: {
      updateData: onUpdateData,
    },
  }

  const table = useLumenTable<TData>(tableOptions)

  useImperativeHandle(instanceRef, () => table, [table])

  const state = table.getState()

  const { rows: allRows } = table.getRowModel()

  const rows = useMemo(() => {
    if (limit) {
      return allRows.slice(0, limit)
    }
    return allRows
  }, [allRows, limit])

  const renderSpinner = (table: Table<TData>) => (
    <td colSpan={table.getAllFlatColumns().length} className="align-middle">
      <Spinner
        className="sticky left-0 h-[45px]"
        key={`placeholder-1`}
        style={{
          width: containerWidth - 2,
        }}
      />
    </td>
  )

  const virtualizer = useVirtualizer({
    count: virtualizeRows ? rows.length : 0,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 44,
    overscan: 20,
  })

  const virtualRows = virtualizeRows ? virtualizer.getVirtualItems() : []
  const [before, after] =
    virtualRows.length > 0
      ? [
          notUndefined(virtualRows[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(virtualRows[virtualRows.length - 1]).end,
        ]
      : [0, 0]

  const showActionsHeader =
    enableColumnVisibility || enableFullscreenToggle || showFilters || showGlobalSearch

  const showLoadingRow = showFooter ? data.length === 0 && isLoading : isLoading

  const hasLeftPinned = !customRowRender && table.getLeftLeafColumns().length > 0
  const hasRightPinned = !customRowRender && table.getRightLeafColumns().length > 0

  // Sync row heights across split tables after every render
  useLayoutEffect(() => {
    if (customRowRender) return

    const bodies = [
      leftBodyRef.current,
      centerBodyRef.current,
      rightBodyRef.current,
    ].filter((b): b is HTMLTableSectionElement => b !== null)

    if (bodies.length < 2) return

    // Reset heights to natural sizing
    for (const body of bodies) {
      for (const row of Array.from(body.rows)) {
        row.style.height = ""
      }
    }

    // Measure natural heights
    const heights = bodies.map((body) =>
      Array.from(body.rows).map((row) => row.getBoundingClientRect().height),
    )

    // Apply max height per row index
    const rowCount = Math.max(...heights.map((h) => h.length))
    for (let i = 0; i < rowCount; i++) {
      const maxHeight = Math.max(...heights.map((h) => h[i] ?? 0))
      for (const body of bodies) {
        const row = body.rows[i]
        if (row) row.style.height = `${maxHeight}px`
      }
    }
  })

  const tableClassNames = cn("border-separate border-spacing-0", {
    "table-auto": tableLayout === "auto",
    "table-fixed": tableLayout === "fixed",
  })

  const renderSplitTableBody = (
    position: TablePosition,
    bodyRef?: RefObject<HTMLTableSectionElement | null>,
  ) => {
    const colCount =
      position === "left"
        ? table.getLeftLeafColumns().length
        : position === "right"
          ? table.getRightLeafColumns().length
          : table.getCenterLeafColumns().length

    return hasError && !isLoading ? (
      <tbody ref={bodyRef}></tbody>
    ) : (
      <tbody ref={bodyRef}>
        {before > 0 && (
          <tr>
            <td
              colSpan={colCount}
              style={{ height: before, printColorAdjust: "exact" }}
            />
          </tr>
        )}
        {(virtualizeRows ? virtualRows : rows).map((rowOrVirtualRow) => {
          const row = virtualizeRows
            ? rows[rowOrVirtualRow.index]
            : (rowOrVirtualRow as Row<TData>)

          if (!row || getIsRowLoading?.(row)) {
            return (
              <tr key={`loading-row-${row.index}`}>
                <td colSpan={colCount} className="align-middle">
                  <Spinner className="h-[45px] w-full" />
                </td>
              </tr>
            )
          }

          return (
            <Memo_TableBodyRow<TData>
              table={table}
              key={`${row.id}-${enableRowSelection}-${enableMultiRowSelection}`}
              row={row}
              onRowClick={onRowClick}
              renderKebabMenuContent={renderKebabMenuContent}
              setActiveMoreMenuRowId={setActiveMoreMenuRowId}
              active={activeRowId === row.id}
              moreMenuActive={row.id === activeMoreMenuRowId}
              classNameCell={classNameCell}
              position={position}
            />
          )
        })}
        {position === "center" &&
          total !== undefined &&
          total - data.length > 0 &&
          fetchMore && (
            <tr
              ref={inViewRef}
              key={`placeholder-container-${total - data.length}-${isLoading}`}
            >
              <td colSpan={colCount} className="align-middle">
                <Spinner
                  className="sticky left-0 h-[45px]"
                  key={`placeholder`}
                  style={{ width: containerWidth - 2 }}
                />
              </td>
            </tr>
          )}
        {position === "center" && showLoadingRow ? (
          <tr>
            <td colSpan={colCount} className="align-middle">
              <Spinner
                className="sticky left-0 h-[45px]"
                style={{ width: containerWidth - 2 }}
              />
            </td>
          </tr>
        ) : null}
        {after > 0 && (
          <tr>
            <td
              colSpan={colCount}
              style={{ height: after, printColorAdjust: "exact" }}
            />
          </tr>
        )}
      </tbody>
    )
  }

  return (
    <div
      id={table.tableId}
      className={cn(
        "isolate flex h-fit max-h-full w-full flex-col overflow-auto rounded",
        className,
        {
          "fixed top-0 left-0 z-modal h-screen max-h-screen w-screen max-w-screen bg-white":
            state.isFullscreen,
        },
      )}
    >
      {showActionsHeader && (
        <Toolbar
          table={table}
          data={data}
          classNameHeader={classNameHeader}
          showFilters={showFilters}
          showGlobalSearch={showGlobalSearch || false}
          enableColumnVisibility={enableColumnVisibility}
          enableFullscreenToggle={enableFullscreenToggle}
          enableExportButton={enableExportButton}
          exportFilename={exportFilename}
          renderCustomActions={renderCustomActions}
        />
      )}
      <BulkActionsFloater table={table} rowSelectedText={rowSelectedText} />

      <div
        className={cn(
          "flex h-fit min-h-0 w-full grow flex-col overflow-auto",
          tableContainerClassName,
        )}
        ref={tableContainerRef}
      >
        <div className="relative flex grow flex-col">
          {customRowRender ? (
            (virtualizeRows ? virtualRows : rows).map((rowOrVirtualRow) => {
              const row = virtualizeRows
                ? rows[rowOrVirtualRow.index]
                : (rowOrVirtualRow as Row<TData>)

              if (!row || getIsRowLoading?.(row)) {
                return <tr key={`loading-row-${row.index}`}>{renderSpinner(table)}</tr>
              }
              return customRowRender?.(row)
            })
          ) : (
            <>
              <div
                className="inline-flex min-w-full"
                style={{
                  width: innerTotalWidth,
                }}
              >
                {hasLeftPinned && (
                  <div className="sticky left-0 z-30 shrink-0 border-r border-colors-border-secondary bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                    <table className={tableClassNames}>
                      <TableHead
                        table={table}
                        classNameHeader={classNameHeader}
                        showColumnKebabMenu={showColumnKebabMenu}
                        position="left"
                      />
                      {renderSplitTableBody("left", leftBodyRef)}
                    </table>
                  </div>
                )}
                <div className="shrink-0 grow">
                  <table
                    className={cn(tableClassNames, "w-full grow")}
                    style={{
                      minWidth: table.getCenterTotalSize(),
                    }}
                    ref={tableRef}
                  >
                    <TableHead
                      table={table}
                      classNameHeader={classNameHeader}
                      showColumnKebabMenu={showColumnKebabMenu}
                      position="center"
                    />
                    {renderSplitTableBody("center", centerBodyRef)}
                  </table>
                </div>
                {hasRightPinned && (
                  <div className="sticky right-0 z-30 shrink-0 border-l border-colors-border-secondary bg-white shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                    <table className={tableClassNames}>
                      <TableHead
                        table={table}
                        classNameHeader={classNameHeader}
                        showColumnKebabMenu={showColumnKebabMenu}
                        position="right"
                      />
                      {renderSplitTableBody("right", rightBodyRef)}
                    </table>
                  </div>
                )}
              </div>
              {(hasError || rows.length === 0) && containerWidth && !isLoading ? (
                <div className="relative flex h-full grow flex-col">
                  <div className="grid grow grid-cols-1 grid-rows-1">
                    {hasError ? errorState : rows.length === 0 ? emptyState : null}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
      {showFooter && <Footer table={table} className={classNameFooter} />}
    </div>
  )
}

DataTable.ActiveFiltersList = ActiveFiltersList
DataTable.FilterButton = FilterButton
DataTable.ClearFiltersButton = ClearFiltersButton
DataTable.GlobalFilter = GlobalFilter
